import type {
	FilterRuleModel,
	ProductsQueryRequestModel,
} from '@/shared/generated'

import { Link } from '@tanstack/react-router'
import { AlertTriangleIcon, DotIcon, ExternalLinkIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useAnchorsQuery } from '@/entities/anchor'
import { useAssortmentListQuery } from '@/entities/assortment'
import { getAssortmentShare } from '@/entities/pricing-campaign'

import { PercentageValueText } from '@/shared/components/common/PercentageValueText'
import { Badge } from '@/shared/components/ui/badge'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { SavedFilterType } from '@/shared/generated'
import { ampli } from '@/shared/lib/ampli'
import { formatNumber } from '@/shared/tools/locale'
import { getFiltersQueryObject } from '@/shared/tools/location'

type SavedReportsReportCounterProps = {
	filters: FilterRuleModel[]
}
const defaultBody: ProductsQueryRequestModel = {
	limit: 1,
	offset: 0,
	order_by: [],
	filters: [],
}

export function SavedReportsReportCounter({
	filters,
	type,
}: SavedReportsReportCounterProps & {
	type: SavedFilterType
}) {
	if (type === SavedFilterType.Products) {
		return <SavedReportsProductsCounter filters={filters} />
	} else if (type === SavedFilterType.Anchors) {
		return <SavedReportsAnchorsCounter filters={filters} />
	}
}

function SavedReportsProductsCounter({
	filters,
}: SavedReportsReportCounterProps) {
	const { t } = useTranslation()

	const totalQuery = useAssortmentListQuery(defaultBody)
	const filteredQuery = useAssortmentListQuery({
		...defaultBody,
		filters,
	})
	const isLoading = totalQuery.isLoading || filteredQuery.isLoading
	const isError = totalQuery.isError || filteredQuery.isError
	const totalLinesCount = totalQuery.data?.total_count || 0
	const linesCount = filteredQuery.data?.total_count || 0
	const skusCount = filteredQuery.data?.total_skus || 0
	if (isError) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<span className='flex size-6 items-center justify-center gap-2 rounded bg-destructive/10 text-xs text-muted-foreground'>
						<AlertTriangleIcon className='size-4 text-destructive' />
					</span>
				</TooltipTrigger>
				<TooltipContent>
					{totalQuery.error?.message ||
						filteredQuery.error?.message ||
						t('error.unrecoverable.title')}
				</TooltipContent>
			</Tooltip>
		)
	} else if (isLoading) {
		return (
			<div className='flex flex-col justify-center gap-1'>
				<Skeleton className='h-4 w-24' />
				<Skeleton className='h-3 w-32' />
			</div>
		)
	} else if (totalLinesCount === 0) {
		return <span className='text-xs text-muted-foreground'>-</span>
	} else {
		return (
			<div className='inline-flex flex-col justify-center gap-1'>
				<span className='flex items-center gap-1'>
					<Tooltip>
						<TooltipTrigger asChild>
							<Badge
								variant='link'
								asChild
								className='truncate !px-0 font-mono'
							>
								<Link
									to='/p/products'
									search={getFiltersQueryObject(filters)}
									onClick={() =>
										ampli.reportsOpenReportNewWindow({
											from: 'counter cell',
										})
									}
									target='_blank'
								>
									<PercentageValueText
										value={linesCount}
										percentage={getAssortmentShare(totalLinesCount, linesCount)}
									/>
									<ExternalLinkIcon className='hidden size-4 text-muted-foreground group-hover/row:block' />
								</Link>
							</Badge>
						</TooltipTrigger>
						<TooltipContent>
							{t('action.open_in_new_window', {
								defaultValue: 'Open in new window',
							})}
						</TooltipContent>
					</Tooltip>
				</span>
				<span className='flex items-center gap-1 text-xs text-muted-foreground'>
					{t('general_skus', { defaultValue: 'SKUs' })}
					{`: ${formatNumber(skusCount)}`}
					<DotIcon className='size-4 text-muted-foreground' />
					{t('app.pls', { defaultValue: 'PLs' })}
					{`: ${formatNumber(linesCount)}`}
				</span>
			</div>
		)
	}
}

function SavedReportsAnchorsCounter({
	filters,
}: SavedReportsReportCounterProps) {
	const { t } = useTranslation()

	const totalQuery = useAnchorsQuery({
		limit: 1,
		offset: 0,
		order_by: [],
		filters: [],
	})
	const filteredQuery = useAnchorsQuery({
		limit: 1,
		offset: 0,
		order_by: [],
		filters,
	})
	const isLoading = totalQuery.isLoading || filteredQuery.isLoading
	const isError = totalQuery.isError || filteredQuery.isError
	const totalCount = totalQuery.data?.total || 0
	const filterdCount = filteredQuery.data?.total || 0
	if (isError) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<span className='flex size-6 items-center justify-center gap-2 rounded bg-destructive/10 text-xs text-muted-foreground'>
						<AlertTriangleIcon className='size-4 text-destructive' />
					</span>
				</TooltipTrigger>
				<TooltipContent>
					{totalQuery.error?.message ||
						filteredQuery.error?.message ||
						t('error.unrecoverable.title')}
				</TooltipContent>
			</Tooltip>
		)
	} else if (isLoading) {
		return (
			<div className='flex flex-col justify-center gap-1'>
				<Skeleton className='h-4 w-24' />
				<Skeleton className='h-3 w-32' />
			</div>
		)
	} else if (totalCount === 0) {
		return <span className='text-xs text-muted-foreground'>-</span>
	} else {
		return (
			<div className='inline-flex flex-col justify-center gap-1'>
				<span className='flex items-center gap-1'>
					<Tooltip>
						<TooltipTrigger asChild>
							<Badge
								variant='link'
								asChild
								className='truncate px-0! font-mono'
							>
								<Link
									to='/p/anchor-management/assortment'
									search={getFiltersQueryObject(filters)}
									onClick={() =>
										ampli.reportsOpenReportNewWindow({
											from: 'counter cell',
										})
									}
									target='_blank'
								>
									<PercentageValueText
										value={filterdCount}
										percentage={getAssortmentShare(totalCount, filterdCount)}
									/>
									<ExternalLinkIcon className='hidden size-4 text-muted-foreground group-hover/row:block' />
								</Link>
							</Badge>
						</TooltipTrigger>
						<TooltipContent>
							{t('action.open_in_new_window', {
								defaultValue: 'Open in new window',
							})}
						</TooltipContent>
					</Tooltip>
				</span>
				<span className='flex items-center gap-1 text-xs text-muted-foreground'>
					{t('app.pls', { defaultValue: 'PLs' })}
					{`: ${formatNumber(filterdCount)}`}
				</span>
			</div>
		)
	}
}
